/**
 * Google Tag Manager dataLayer Helper.
 *
 * Pusht E-Commerce Events nach GA4-Schema mit allen verfuegbaren Werten.
 * @see https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 */

declare global {
    interface Window {
        dataLayer?: Array<Record<string, unknown>>;
    }
}

export interface GtmItem {
    item_id: string | number;
    item_name: string;
    /** Slug zur Identifikation */
    item_slug?: string;
    /** SKU/Produktnummer */
    item_sku?: string;
    /** Produktgruppe (Hauptkategorie) */
    item_category?: string;
    /** Unterkategorie (z.B. Produktgruppen-Hierarchie) */
    item_category2?: string;
    item_category3?: string;
    /** Pricing-Variante (Monatlich/Jaehrlich) */
    item_variant?: string;
    /** Brand (Hersteller / Modul) */
    item_brand?: string;
    /** Listen-Position (1-basiert) */
    index?: number;
    item_list_id?: string;
    item_list_name?: string;
    /** Brutto-Preis pro Stueck (in Euro, nicht Cent) */
    price: number;
    /** Netto-Preis pro Stueck */
    price_net?: number;
    /** Rabatt pro Stueck */
    discount?: number;
    /** Waehrung (Default: EUR) */
    currency?: string;
    quantity?: number;
}

export interface GtmCustomer {
    /** NetCell Customer ID — wird zu user_id */
    id?: number;
    /** Customer Number (z.B. C001234) */
    customer_number?: string;
    email?: string;
    /** SHA256 hash der E-Mail fuer Enhanced Conversions */
    email_sha256?: string;
    type?: 'private' | 'business';
    country?: string;
    city?: string;
    postal_code?: string;
    is_new?: boolean;
}

interface PushEvent {
    event: string;
    ecommerce?: Record<string, unknown> | null;
    [key: string]: unknown;
}

function push(event: PushEvent): void {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    // Best practice: Vorher ecommerce zuruecksetzen
    if (event.ecommerce !== undefined) {
        window.dataLayer.push({ ecommerce: null });
    }
    window.dataLayer.push(event);
}

/**
 * Cents → Euro mit 2 Nachkommastellen.
 */
function eur(cents: number): number {
    return Math.round(cents) / 100;
}

/**
 * Identifiziert den eingeloggten Kunden (set einmalig pro Session).
 */
export function gtmIdentify(customer: GtmCustomer): void {
    push({
        event: 'identify',
        user_id: customer.id ? String(customer.id) : undefined,
        customer: {
            customer_number: customer.customer_number,
            type: customer.type,
            country: customer.country,
            is_new: customer.is_new ?? false,
        },
        // Enhanced Conversions
        user_data: customer.email_sha256
            ? { sha256_email_address: customer.email_sha256 }
            : undefined,
    });
}

/**
 * view_item_list — Produktliste angezeigt (Shop, Produktgruppe, Suche).
 */
export function gtmViewItemList(listName: string, items: GtmItem[], listId?: string): void {
    push({
        event: 'view_item_list',
        ecommerce: {
            item_list_id: listId ?? listName.toLowerCase().replace(/\s+/g, '_'),
            item_list_name: listName,
            items: items.map((it, idx) => ({
                ...it,
                index: it.index ?? idx + 1,
                item_list_id: listId ?? listName.toLowerCase().replace(/\s+/g, '_'),
                item_list_name: listName,
                currency: it.currency ?? 'EUR',
                quantity: it.quantity ?? 1,
            })),
        },
    });
}

/**
 * select_item — Klick auf ein Produkt in einer Liste.
 */
export function gtmSelectItem(listName: string, item: GtmItem): void {
    push({
        event: 'select_item',
        ecommerce: {
            item_list_name: listName,
            items: [{ ...item, currency: item.currency ?? 'EUR' }],
        },
    });
}

/**
 * view_item — Produktdetailseite.
 */
export function gtmViewItem(item: GtmItem): void {
    push({
        event: 'view_item',
        ecommerce: {
            currency: item.currency ?? 'EUR',
            value: item.price * (item.quantity ?? 1),
            items: [{ ...item, currency: item.currency ?? 'EUR', quantity: item.quantity ?? 1 }],
        },
    });
}

/**
 * add_to_cart.
 */
export function gtmAddToCart(item: GtmItem): void {
    const qty = item.quantity ?? 1;
    push({
        event: 'add_to_cart',
        ecommerce: {
            currency: item.currency ?? 'EUR',
            value: item.price * qty,
            items: [{ ...item, currency: item.currency ?? 'EUR', quantity: qty }],
        },
    });
}

/**
 * remove_from_cart.
 */
export function gtmRemoveFromCart(item: GtmItem): void {
    const qty = item.quantity ?? 1;
    push({
        event: 'remove_from_cart',
        ecommerce: {
            currency: item.currency ?? 'EUR',
            value: item.price * qty,
            items: [{ ...item, currency: item.currency ?? 'EUR', quantity: qty }],
        },
    });
}

/**
 * view_cart.
 *
 * @param items Cart-Items
 * @param totals { subtotal_net, tax, total_gross } in Cents
 */
export function gtmViewCart(items: GtmItem[], totals: { subtotal_net?: number; tax?: number; total_gross: number; currency?: string }): void {
    push({
        event: 'view_cart',
        ecommerce: {
            currency: totals.currency ?? 'EUR',
            value: eur(totals.total_gross),
            tax: totals.tax !== undefined ? eur(totals.tax) : undefined,
            subtotal: totals.subtotal_net !== undefined ? eur(totals.subtotal_net) : undefined,
            items: items.map(it => ({ ...it, currency: it.currency ?? 'EUR', quantity: it.quantity ?? 1 })),
        },
    });
}

/**
 * begin_checkout.
 */
export function gtmBeginCheckout(items: GtmItem[], totals: { subtotal_net?: number; tax?: number; total_gross: number; currency?: string; coupon?: string }): void {
    push({
        event: 'begin_checkout',
        ecommerce: {
            currency: totals.currency ?? 'EUR',
            value: eur(totals.total_gross),
            tax: totals.tax !== undefined ? eur(totals.tax) : undefined,
            coupon: totals.coupon,
            items: items.map(it => ({ ...it, currency: it.currency ?? 'EUR', quantity: it.quantity ?? 1 })),
        },
    });
}

/**
 * add_shipping_info — Adresse erfasst.
 */
export function gtmAddShippingInfo(items: GtmItem[], totals: { total_gross: number; currency?: string }, shippingTier?: string): void {
    push({
        event: 'add_shipping_info',
        ecommerce: {
            currency: totals.currency ?? 'EUR',
            value: eur(totals.total_gross),
            shipping_tier: shippingTier,
            items: items.map(it => ({ ...it, currency: it.currency ?? 'EUR', quantity: it.quantity ?? 1 })),
        },
    });
}

/**
 * add_payment_info.
 */
export function gtmAddPaymentInfo(paymentType: string, items: GtmItem[], totals: { total_gross: number; currency?: string }): void {
    push({
        event: 'add_payment_info',
        ecommerce: {
            currency: totals.currency ?? 'EUR',
            value: eur(totals.total_gross),
            payment_type: paymentType,
            items: items.map(it => ({ ...it, currency: it.currency ?? 'EUR', quantity: it.quantity ?? 1 })),
        },
    });
}

/**
 * purchase — Bestellung abgeschlossen.
 *
 * @param data Order-Daten
 */
export function gtmPurchase(data: {
    order_number: string;
    invoice_number?: string;
    subtotal_net: number;
    tax: number;
    shipping?: number;
    total_gross: number;
    currency?: string;
    coupon?: string;
    payment_method?: string;
    items: GtmItem[];
}): void {
    push({
        event: 'purchase',
        ecommerce: {
            transaction_id: data.order_number,
            affiliation: 'NetCell Shop',
            value: eur(data.total_gross),
            subtotal: eur(data.subtotal_net),
            tax: eur(data.tax),
            shipping: data.shipping !== undefined ? eur(data.shipping) : 0,
            currency: data.currency ?? 'EUR',
            coupon: data.coupon,
            payment_type: data.payment_method,
            invoice_id: data.invoice_number,
            items: data.items.map(it => ({ ...it, currency: it.currency ?? 'EUR', quantity: it.quantity ?? 1 })),
        },
    });
}

/**
 * refund — Rueckerstattung.
 */
export function gtmRefund(orderNumber: string, totalGross: number, items?: GtmItem[]): void {
    push({
        event: 'refund',
        ecommerce: {
            transaction_id: orderNumber,
            value: eur(totalGross),
            currency: 'EUR',
            items: items?.map(it => ({ ...it, currency: it.currency ?? 'EUR', quantity: it.quantity ?? 1 })),
        },
    });
}

/**
 * Generisches Custom-Event.
 */
export function gtmEvent(name: string, params: Record<string, unknown> = {}): void {
    push({ event: name, ...params });
}

// =========================================================================
// REMARKETING & ENHANCED CONVERSIONS
// =========================================================================

/**
 * Setzt persistente User-Properties — werden mit jedem Event gesendet.
 * Wichtig fuer Remarketing-Audiences in GA4 / Google Ads.
 */
export function gtmSetUserProperties(props: {
    customer_id?: number | string;
    customer_number?: string;
    customer_type?: 'private' | 'business';
    customer_country?: string;
    customer_lifetime_value?: number;
    customer_orders_count?: number;
    customer_segment?: string;
    is_logged_in?: boolean;
    is_returning?: boolean;
    preferred_locale?: string;
    has_active_subscription?: boolean;
}): void {
    push({
        event: 'user_properties_set',
        user_properties: props,
        // Auch als Top-Level fuer Tags die direkt darauf zugreifen
        ...(props.customer_id ? { user_id: String(props.customer_id) } : {}),
    });
}

/**
 * Enhanced Conversions Daten setzen (gehashte PII fuer besseres Tracking).
 * Muss VOR purchase/conversion Events gepusht werden.
 */
export function gtmSetEnhancedConversions(data: {
    email_sha256?: string;
    phone_sha256?: string;
    address?: {
        first_name_sha256?: string;
        last_name_sha256?: string;
        street?: string;
        city?: string;
        region?: string;
        postal_code?: string;
        country?: string;
    };
}): void {
    push({
        event: 'enhanced_conversions_set',
        user_data: {
            sha256_email_address: data.email_sha256,
            sha256_phone_number: data.phone_sha256,
            address: data.address ? {
                sha256_first_name: data.address.first_name_sha256,
                sha256_last_name: data.address.last_name_sha256,
                street: data.address.street,
                city: data.address.city,
                region: data.address.region,
                postal_code: data.address.postal_code,
                country: data.address.country,
            } : undefined,
        },
    });
}

/**
 * Sign up — Neuregistrierung.
 */
export function gtmSignUp(method: string, customerId?: number): void {
    push({
        event: 'sign_up',
        method,
        ...(customerId ? { user_id: String(customerId) } : {}),
    });
}

/**
 * Login.
 */
export function gtmLogin(method: string, customerId?: number): void {
    push({
        event: 'login',
        method,
        ...(customerId ? { user_id: String(customerId) } : {}),
    });
}

/**
 * Lead — z.B. Kontaktanfrage, Newsletter-Anmeldung.
 */
export function gtmGenerateLead(value?: number, currency: string = 'EUR'): void {
    push({
        event: 'generate_lead',
        currency,
        value: value !== undefined ? eur(value) : undefined,
    });
}

/**
 * Search — Such-Event (z.B. Domain-Suche).
 */
export function gtmSearch(searchTerm: string, resultsCount?: number): void {
    push({
        event: 'search',
        search_term: searchTerm,
        results_count: resultsCount,
    });
}

/**
 * View promotion — Banner / Promo angezeigt.
 */
export function gtmViewPromotion(promo: { id: string; name: string; creative?: string; slot?: string }): void {
    push({
        event: 'view_promotion',
        ecommerce: {
            promotion_id: promo.id,
            promotion_name: promo.name,
            creative_name: promo.creative,
            creative_slot: promo.slot,
        },
    });
}

/**
 * Select promotion — Klick auf Promo.
 */
export function gtmSelectPromotion(promo: { id: string; name: string; creative?: string; slot?: string }): void {
    push({
        event: 'select_promotion',
        ecommerce: {
            promotion_id: promo.id,
            promotion_name: promo.name,
            creative_name: promo.creative,
            creative_slot: promo.slot,
        },
    });
}

/**
 * Share — Inhalt geteilt.
 */
export function gtmShare(method: string, contentType: string, itemId: string): void {
    push({
        event: 'share',
        method,
        content_type: contentType,
        item_id: itemId,
    });
}

/**
 * Funnel Step — generischer Schritt im Checkout/Onboarding.
 */
export function gtmFunnelStep(funnel: string, step: number, stepName: string, additionalData: Record<string, unknown> = {}): void {
    push({
        event: 'funnel_step',
        funnel_name: funnel,
        step_number: step,
        step_name: stepName,
        ...additionalData,
    });
}

/**
 * Form interaction — Form gestartet/abgeschickt/Fehler.
 */
export function gtmFormEvent(action: 'start' | 'submit' | 'error' | 'success', formName: string, additionalData: Record<string, unknown> = {}): void {
    push({
        event: `form_${action}`,
        form_name: formName,
        ...additionalData,
    });
}

/**
 * Page Type — sollte bei jedem Inertia-Navigate gesetzt werden.
 * Erlaubt Audience-Targeting nach Seitentyp.
 */
export function gtmSetPageType(pageType: string, additionalContext: Record<string, unknown> = {}): void {
    push({
        event: 'page_type_set',
        page_type: pageType,
        ...additionalContext,
    });
}

/**
 * Content Group — fuer GA4 Content-Gruppierung.
 */
export function gtmSetContentGroup(group1?: string, group2?: string, group3?: string): void {
    push({
        event: 'content_group_set',
        content_group: group1,
        content_group2: group2,
        content_group3: group3,
    });
}
